import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Ornament } from "../components/Ornament";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PASSWORD_MIN = 8;

function passwordChecks(pw) {
  return {
    length: pw.length >= PASSWORD_MIN,
    letter: /[A-Za-z]/.test(pw),
    number: /\d/.test(pw),
  };
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const token = (searchParams.get("token") || "").trim();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const pwChecks = useMemo(() => passwordChecks(password), [password]);
  const pwOk = pwChecks.length && pwChecks.letter && pwChecks.number;
  const matches = password.length > 0 && password === confirm;

  const inputBase = "w-full px-4 py-3 hairline-cream bg-white";
  const inputStyle = { borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "16px", minHeight: 48 };
  const labelClass = "block mb-2 text-[#1B2A4A]";
  const labelStyle = { fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: 500 };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("This reset link is not valid. Please request a new one from the sign-in page."); return; }
    if (!pwOk) { setError(`Password must be at least ${PASSWORD_MIN} characters and include a letter and a number.`); return; }
    if (!matches) { setError("The two passwords don't match."); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/reset-password", { token, new_password: password });
      if (res.data?.session_token) localStorage.setItem("session_token", res.data.session_token);
      setUser({
        user_id: res.data.user_id,
        email: res.data.email,
        name: res.data.name,
        email_verified: !!res.data.email_verified,
      });
      setDone(true);
      // Give the reader a moment to see the confirmation before we move them on.
      setTimeout(() => navigate("/my-living-book", { replace: true }), 1600);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? (detail[0]?.msg || "Please check your details and try again.")
        : (typeof detail === "string" ? detail : "Something went wrong. Please request a new reset link and try again.");
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#111D33" }} data-testid="reset-password-page">
      <header className="px-6 sm:px-10 py-6">
        <Link to="/" className="inline-flex items-center gap-3" data-testid="reset-home-link">
          <span className="text-[#B8973A] text-xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
          <span className="text-[#E8D5A3]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", letterSpacing: "0.06em" }}>The Living Book</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md p-8 sm:p-10 bg-[#FAF7F2]" style={{ borderRadius: 4 }} data-testid="reset-card">
          {done ? (
            <div data-testid="reset-success">
              <p className="label-ui text-[11px] text-[#B8973A] text-center tracking-[0.25em]">PASSWORD UPDATED</p>
              <h1 className="text-3xl sm:text-4xl text-center mt-4 mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Your new password is saved.
              </h1>
              <p className="text-[#1B2A4A] text-center" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.7 }}>
                Taking you to your Living Book now…
              </p>
            </div>
          ) : (
            <>
              <p className="label-ui text-[11px] text-[#B8973A] text-center">Choose a new password</p>
              <h1 className="text-3xl sm:text-4xl text-center mt-4 mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Set a new password
              </h1>
              <p className="text-center text-[#1B2A4A] mb-6" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem", fontStyle: "italic" }}>
                Choose something you'll remember. This link is safe to use for 60 minutes.
              </p>
              {!token && (
                <p className="text-red-700 text-sm mb-4" role="alert" data-testid="reset-missing-token">
                  This link is missing its reset token. Please request a new one from the sign-in page.
                </p>
              )}
              <form onSubmit={submit} className="space-y-5" noValidate data-testid="reset-form">
                <div>
                  <label htmlFor="new-password" className={labelClass} style={labelStyle}>New password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={PASSWORD_MIN}
                      className={inputBase}
                      style={{ ...inputStyle, paddingRight: 52 }}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      className="absolute inset-y-0 right-0 flex items-center justify-center text-[#8C8278] hover:text-[#1B2A4A] transition-colors"
                      style={{ width: 48, background: "transparent", border: "none", cursor: "pointer" }}
                      data-testid="reset-password-toggle"
                    >
                      {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                    </button>
                  </div>
                  <ul className="mt-2 space-y-1 text-sm" style={{ fontFamily: "Georgia, serif" }} data-testid="reset-password-rules">
                    <li style={{ color: pwChecks.length ? "#4B7A2B" : "#8C8278" }}>
                      {pwChecks.length ? "✓" : "·"} At least {PASSWORD_MIN} characters
                    </li>
                    <li style={{ color: pwChecks.letter ? "#4B7A2B" : "#8C8278" }}>
                      {pwChecks.letter ? "✓" : "·"} Includes a letter
                    </li>
                    <li style={{ color: pwChecks.number ? "#4B7A2B" : "#8C8278" }}>
                      {pwChecks.number ? "✓" : "·"} Includes a number
                    </li>
                  </ul>
                </div>

                <div>
                  <label htmlFor="confirm-password" className={labelClass} style={labelStyle}>Confirm new password</label>
                  <input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={PASSWORD_MIN}
                    className={inputBase}
                    style={inputStyle}
                    data-testid="reset-confirm-input"
                  />
                  {confirm.length > 0 && !matches && (
                    <p className="text-red-700 text-sm mt-2" data-testid="reset-mismatch-error">The two passwords don't match.</p>
                  )}
                </div>

                {error && (
                  <div
                    role="alert"
                    data-testid="reset-error"
                    className="p-4"
                    style={{ background: "#FDECEC", border: "1px solid #E8B4B4", borderRadius: 4 }}
                  >
                    <p className="text-[#8A1F1F]" style={{ fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: 1.55 }}>{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy || !token}
                  className="btn-primary w-full"
                  style={{ minHeight: 52 }}
                  data-testid="reset-submit-btn"
                >
                  {busy ? "Saving…" : "Save my new password"}
                </button>
              </form>
              <p className="text-center pt-6">
                <Link to="/login" className="label-ui text-[12px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="reset-back-to-login">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </main>
      <footer className="py-8 text-center">
        <Ornament color="#B8973A" />
        <p className="mt-4 text-[#8C8278] italic text-sm" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          Some books are written. Yours is lived.
        </p>
      </footer>
    </div>
  );
}
